import { useEffect, useRef, useState } from "react";
import { Link, NavLink, Outlet, useLocation } from "react-router";
import { api } from "../lib/api";
import { PRODUCT_NAME } from "../lib/config";
import { useSession } from "../lib/session";

const LOCKED = {
  no_subscription: "Choose a plan to start adding your work.",
  payment_failed: "Your subscription payment hasn't gone through, so your agency is read-only.",
  ended: "Your subscription has ended, so your agency is read-only.",
} as const;

/** Signed-in frame: wordmark, section nav, company and sign-out. */
export function Shell() {
  const { session, signOut } = useSession();
  const me = session.status === "member" ? session.me : null;
  const approved = me?.tenant.approval === "approved";
  const billing = me?.tenant.billing;
  const locked = approved && billing?.enabled && billing.access.state === "locked" ? billing.access.reason : null;
  const isOwner = me?.user.role === "owner";
  const [legalOutdated, setLegalOutdated] = useState(false);
  const [waiting, setWaiting] = useState(0);
  // Phones: the nav and account sit behind a Menu button, closed again after each navigation.
  const [menuOpen, setMenuOpen] = useState(false);
  // Agency settings (privacy, account, billing, approvals) sit in their own menu by the agency name.
  const [settingsOpen, setSettingsOpen] = useState(false);
  const settingsRef = useRef<HTMLDivElement>(null);
  const { pathname } = useLocation();
  useEffect(() => {
    setMenuOpen(false);
    setSettingsOpen(false);
  }, [pathname]);
  useEffect(() => {
    if (!settingsOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setSettingsOpen(false);
        settingsRef.current?.querySelector<HTMLButtonElement>(".settings-toggle")?.focus();
      }
    };
    const onClick = (e: MouseEvent) => {
      if (!settingsRef.current?.contains(e.target as Node)) setSettingsOpen(false);
    };
    document.addEventListener("keydown", onKey);
    document.addEventListener("mousedown", onClick);
    return () => {
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("mousedown", onClick);
    };
  }, [settingsOpen]);
  const onSettingsPage = ["/privacy", "/security", "/appearance", "/extension", "/calendar", "/email-log", "/billing", "/approvals"].some((p) => pathname.startsWith(p));
  useEffect(() => {
    if (!me || !approved) return;
    api
      .privacy()
      .then((p) => setLegalOutdated(!p.legalUpToDate))
      .catch(() => {});
  }, [me, approved]);
  useEffect(() => {
    if (!me?.user.platformAdmin) return;
    api
      .agencies("pending")
      .then((r) => setWaiting(r.agencies.length))
      .catch(() => {});
  }, [me]);

  return (
    <>
      <a className="sr-only" href="#content">
        Skip to content
      </a>
      <header className="topbar">
        <Link to="/today" className="wordmark">
          {PRODUCT_NAME}
        </Link>
        {me && (
          <button
            type="button"
            className="menu-toggle link-btn"
            aria-expanded={menuOpen}
            aria-controls="topbar-menu"
            onClick={() => setMenuOpen((v) => !v)}
          >
            {menuOpen ? "Close" : "Menu"}
          </button>
        )}
        <div id="topbar-menu" className={`topbar-menu${menuOpen ? " open" : ""}`}>
          {approved && (
            <nav aria-label="Main">
              <NavLink to="/today">Today</NavLink>
              <NavLink to="/jobs">Jobs</NavLink>
              <NavLink to="/candidates">Candidates</NavLink>
              <NavLink to="/pools">Pools</NavLink>
              <NavLink to="/companies">Companies</NavLink>
              <NavLink to="/team">Team</NavLink>
              <NavLink to="/reports">Reports</NavLink>
            </nav>
          )}
          {me && (
            <div className="account">
              <span>
                <strong>{me.tenant.name}</strong> <span className="label">· {me.user.role}</span>
              </span>
              {approved && (
                <div className="settings" ref={settingsRef}>
                  <button
                    type="button"
                    className={`settings-toggle link-btn${onSettingsPage ? " current" : ""}`}
                    aria-expanded={settingsOpen}
                    aria-controls="settings-menu"
                    onClick={() => setSettingsOpen((v) => !v)}
                  >
                    Settings{waiting > 0 && <span className="figures"> · {waiting}</span>} <span aria-hidden>▾</span>
                  </button>
                  <nav id="settings-menu" aria-label="Settings" className={`settings-list${settingsOpen ? " open" : ""}`}>
                    <NavLink to="/privacy">Privacy</NavLink>
                    <NavLink to="/security">Your account</NavLink>
                    <NavLink to="/appearance">Appearance</NavLink>
                    <NavLink to="/extension">Browser extension</NavLink>
                    <NavLink to="/calendar">Calendar</NavLink>
                    <NavLink to="/email-log">Email logging</NavLink>
                    {isOwner && billing?.enabled && <NavLink to="/billing">Billing</NavLink>}
                    {me.user.platformAdmin && (
                      <NavLink to="/approvals">Approvals{waiting > 0 && <span className="figures"> · {waiting}</span>}</NavLink>
                    )}
                  </nav>
                </div>
              )}
              <button type="button" className="link-btn" onClick={() => void signOut()}>
                Sign out
              </button>
            </div>
          )}
        </div>
      </header>
      {locked && (
        <p className="banner" role="status">
          {LOCKED[locked]} Your data is all still here, and you can look at and export it.{" "}
          {isOwner ? <Link to="/billing">Go to Billing →</Link> : "Ask an owner of your agency to sort it out."}
        </p>
      )}
      {legalOutdated && !locked && (
        <p className="banner" role="status">
          The terms of service, data processing agreement and privacy policy need accepting. <Link to="/privacy">Review on the Privacy page →</Link>
        </p>
      )}
      {approved && !me.user.displayName && !legalOutdated && !locked && (
        <p className="banner" role="status">
          Colleagues see your email until you add your name. <Link to="/security">Add it on your Account page →</Link>
        </p>
      )}
      <main id="content" className="page">
        <Outlet />
      </main>
    </>
  );
}
